import { Card, Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import { useSiteContext, useTranslation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { constants } from '@eti/utils';
import { screenReadersOnlyStyles } from '../../../../utils/styleUtils';
import { formatAmountInChargingCurrency, priceWithFullNameCurrency } from '../../utils';
import RouteInfoWithSymbols from '../common/RouteInfoWithSymbols';
import type { RefundPayoutsType } from './models';

const spaceBetweenStyles = css`
  justify-content: space-between;
`;

const productStyles = css`
  justify-content: space-between;
  padding-inline-start: 16px;
`;

const cardNumberStyles = css`
  align-self: end;
`;

const dividerStyles = css`
  background-color: ${vars.colors.divider};
  height: 1px;
  margin: 8px 0;
  width: 100%;
`;

interface RefundedAmountBreakdownProps {
  refundPayouts: RefundPayoutsType;
}
const RefundedAmountBreakdown = ({ refundPayouts }: RefundedAmountBreakdownProps) => {
  const { t } = useTranslation();
  const { language } = useSiteContext();

  return (
    <Stack>
      {refundPayouts.map((refundPayout, index) => {
        const {
          manuallyCalculatedRefundAmount,
          refundedProducts,
          refundedProductsTotalAmount,
          refundedProviderBookings,
          refundHandlingFee,
          refundNonBankAddition,
          refundPaymentMethod,
          totalRefundedAmount,
        } = refundPayout;

        return (
          <Card key={`refundPayout-${index}`}>
            <Stack spacing={8}>
              {!manuallyCalculatedRefundAmount.value ? (
                <>
                  <Stack spacing={8}>
                    {refundedProviderBookings.map((refundedProviderBooking) => {
                      const priceInChargingCurrency = formatAmountInChargingCurrency(
                        language.code,
                        refundedProviderBooking.refundedAmount.value,
                        refundedProviderBooking.refundedAmount.currency,
                      );

                      return (
                        <Inline
                          className={spaceBetweenStyles}
                          data-testid={`provider-booking-refund-${index}`}
                          key={refundedProviderBooking.reference}
                          noWrap
                          spacing={4}
                        >
                          <RouteInfoWithSymbols
                            bounds={refundedProviderBooking.bounds}
                            data-testid={`provider-booking-refund-${index}`}
                            journeyType={refundedProviderBooking.journeyType}
                          />
                          <Text aria-hidden>
                            <bdi>{priceInChargingCurrency}</bdi>
                          </Text>
                          <div className={screenReadersOnlyStyles}>
                            {priceWithFullNameCurrency(
                              priceInChargingCurrency,
                              language.code,
                              refundedProviderBooking.refundedAmount.currency,
                            )}
                          </div>
                        </Inline>
                      );
                    })}
                    {refundedProductsTotalAmount && (
                      <>
                        <Inline
                          className={spaceBetweenStyles}
                          data-testid={`products-refund-${index}`}
                          noWrap
                          spacing={4}
                        >
                          <Text>
                            {t('MyBookings.RefundStatus.PayoutDrawer.RefundableProducts')}
                          </Text>
                          <Text aria-hidden>
                            <bdi>
                              {formatAmountInChargingCurrency(
                                language.code,
                                refundedProductsTotalAmount.value,
                                refundedProductsTotalAmount.currency,
                              )}
                            </bdi>
                          </Text>
                          <div className={screenReadersOnlyStyles}>
                            {priceWithFullNameCurrency(
                              formatAmountInChargingCurrency(
                                language.code,
                                refundedProductsTotalAmount.value,
                                refundedProductsTotalAmount.currency,
                              ),
                              language.code,
                              refundedProductsTotalAmount.currency,
                            )}
                          </div>
                        </Inline>
                        <Stack
                          aria-label={t('MyBookings.RefundStatus.PayoutDrawer.RefundableProducts')}
                          role="list"
                          spacing={4}
                        >
                          {refundedProducts.map((refundedProduct) => {
                            return (
                              <Inline
                                className={productStyles}
                                data-testid={`${refundedProduct.code}-refund-${index}`}
                                key={`${refundedProduct.code}-${index}`}
                                noWrap
                                role="listitem"
                                spacing={4}
                              >
                                <Text>{refundedProduct.name}</Text>
                                <Text aria-hidden>
                                  <bdi>
                                    {formatAmountInChargingCurrency(
                                      language.code,
                                      refundedProduct.refundedAmount.value,
                                      refundedProduct.refundedAmount.currency,
                                    )}
                                  </bdi>
                                </Text>
                                <div className={screenReadersOnlyStyles}>
                                  {priceWithFullNameCurrency(
                                    formatAmountInChargingCurrency(
                                      language.code,
                                      refundedProduct.refundedAmount.value,
                                      refundedProduct.refundedAmount.currency,
                                    ),
                                    language.code,
                                    refundedProduct.refundedAmount.currency,
                                  )}
                                </div>
                              </Inline>
                            );
                          })}
                        </Stack>
                      </>
                    )}
                    <Inline
                      className={spaceBetweenStyles}
                      data-testid={`handling-fee-refund-${index}`}
                      noWrap
                      spacing={4}
                    >
                      <Text>{t('RefundAnalysis.HandlingFee.Label')}</Text>
                      <Text aria-hidden>
                        <bdi>
                          {formatAmountInChargingCurrency(
                            language.code,
                            refundHandlingFee.value,
                            refundHandlingFee.currency,
                          )}
                        </bdi>
                      </Text>
                      <div className={screenReadersOnlyStyles}>
                        {priceWithFullNameCurrency(
                          formatAmountInChargingCurrency(
                            language.code,
                            refundHandlingFee.value,
                            refundHandlingFee.currency,
                          ),
                          language.code,
                          refundHandlingFee.currency,
                        )}
                      </div>
                    </Inline>
                    <Inline
                      className={spaceBetweenStyles}
                      data-testid={`non-bank-addition-refund-${index}`}
                      noWrap
                      spacing={4}
                    >
                      <Text>{t('MyBookings.RefundStatus.PayoutDrawer.NonBankAddition')}</Text>
                      <Text aria-hidden>
                        <bdi>
                          {formatAmountInChargingCurrency(
                            language.code,
                            refundNonBankAddition.value,
                            refundNonBankAddition.currency,
                          )}
                        </bdi>
                      </Text>
                      <div className={screenReadersOnlyStyles}>
                        {priceWithFullNameCurrency(
                          formatAmountInChargingCurrency(
                            language.code,
                            refundNonBankAddition.value,
                            refundNonBankAddition.currency,
                          ),
                          language.code,
                          refundNonBankAddition.currency,
                        )}
                      </div>
                    </Inline>
                  </Stack>
                  <span className={dividerStyles} />
                </>
              ) : null}
              <Inline
                className={spaceBetweenStyles}
                data-testid={`total-refund-${index}`}
                noWrap
                spacing={4}
              >
                <Text variant={textVariants._16_BOLD}>
                  {t('MyBookings.RefundStatus.PayoutDrawer.TotalRefund')}
                </Text>
                <Text aria-hidden variant={textVariants._16_BOLD}>
                  <bdi>
                    {!manuallyCalculatedRefundAmount.value
                      ? formatAmountInChargingCurrency(
                          language.code,
                          totalRefundedAmount.value,
                          totalRefundedAmount.currency,
                        )
                      : formatAmountInChargingCurrency(
                          language.code,
                          manuallyCalculatedRefundAmount.value,
                          manuallyCalculatedRefundAmount.currency,
                        )}
                  </bdi>
                </Text>
                <div className={screenReadersOnlyStyles}>
                  {!manuallyCalculatedRefundAmount.value
                    ? priceWithFullNameCurrency(
                        formatAmountInChargingCurrency(
                          language.code,
                          totalRefundedAmount.value,
                          totalRefundedAmount.currency,
                        ),
                        language.code,
                        totalRefundedAmount.currency,
                      )
                    : priceWithFullNameCurrency(
                        formatAmountInChargingCurrency(
                          language.code,
                          manuallyCalculatedRefundAmount.value,
                          manuallyCalculatedRefundAmount.currency,
                        ),
                        language.code,
                        manuallyCalculatedRefundAmount.currency,
                      )}
                </div>
              </Inline>
              <Inline
                className={spaceBetweenStyles}
                data-testid={`payment-method-${index}`}
                noWrap
                spacing={4}
              >
                <Text>{t('MyBookings.RefundStatus.PayoutDrawer.PaymentMethod')}</Text>
                <Text className={constants.MOUSE_FLOW_CLASS_NAMES}>
                  {refundPaymentMethod.paymentType}
                  {refundPaymentMethod.paymentSubType &&
                  refundPaymentMethod.paymentSubType !== 'UNKNOWN'
                    ? ` (${refundPaymentMethod.paymentSubType})`
                    : ''}
                </Text>
              </Inline>
              {refundPaymentMethod.maskedCardNumber && (
                <Text
                  className={cx(cardNumberStyles, constants.MOUSE_FLOW_CLASS_NAMES)}
                  data-testid={`card-number-${index}`}
                >
                  {refundPaymentMethod.maskedCardNumber}
                </Text>
              )}
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );
};

export default RefundedAmountBreakdown;
